import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import SalonCard from './components/SalonCard';
import SummaryMetrics from './components/SummaryMetrics';
import AddSalonModal from './components/AddSalonModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import SearchAndFilter from './components/SearchAndFilter';
import Icon from '../../components/AppIcon';
import api from 'utils/api';

const SalonDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [filteredSalons, setFilteredSalons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  // Mock data for salons
  const [salons, setSalons] = useState([]);

  const fetchSalons = async () => {
    setLoading(true);
    try {
      const res = await api.get('/salons');
      setSalons(res.data);
      localStorage.setItem('@salons', JSON.stringify(res.data));
      console.log(res.data);
    } catch (err) {
      console.error('Error fetching salons:', err);
      alert('خطا در دریافت اطلاعات سالن‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalons();
  }, []);

  // Summary metrics
  const summaryMetrics = {
    totalSalons: salons?.length,
    totalEmployees: salons?.reduce(
      (sum, salon) => sum + salon?.employeeCount,
      0,
    ),
    totalServices: salons?.reduce((sum, salon) => sum + salon?.serviceCount, 0),
    todayAppointments: salons?.reduce(
      (sum, salon) => sum + salon?.todayAppointments,
      0,
    ),
  };

  // Filter salons based on search and filter
  useEffect(() => {
    let filtered = salons;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(
        (salon) =>
          salon?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          salon?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      );
    }

    // Apply status filter
    if (activeFilter !== 'all') {
      filtered = filtered?.filter((salon) => salon?.status === activeFilter);
    }

    setFilteredSalons(filtered);
  }, [salons, searchTerm, activeFilter]);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  const handleAddSalon = async (newSalonData) => {
    console.log('before save', newSalonData);
    try {
      const res = await api.post('/salons', newSalonData);
      const newSalon = res.data;
      console.log(newSalon);
      // Optional: add computed props for UI
      setSalons((prev) => [
        ...prev,
        {
          ...newSalon,
          status: 'active',
          employeeCount: 0,
          serviceCount: 0,
          todayAppointments: 0,
        },
      ]);
    } catch (err) {
      console.error('Error creating salon:', err);
      alert('خطا در ایجاد سالن');
    }
  };

  const handleEditSalon = (salon) => {
    navigate('/salon-management', { state: { selectedSalon: salon } });
  };

  const handleDeleteSalon = (salon) => {
    setSelectedSalon(salon);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = (salon) => {
    setSalons((prev) => prev?.filter((s) => s?.id !== salon?.id));
    setSelectedSalon(null);
  };

  const handleViewDetails = (salon) => {
    navigate('/salon-management', { state: { selectedSalon: salon } });
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Sidebar isOpen={sidebarOpen} onClose={handleCloseSidebar} />
      {/* <Header
        isCollapsed={!sidebarOpen}
        onToggleSidebar={handleToggleSidebar}
      /> */}
      <main className="lg:mr-60 pt-16">
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <BreadcrumbNavigation />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  داشبورد سالن‌ها
                </h1>
                <p className="text-muted-foreground mt-1">
                  مدیریت و نظارت بر تمام سالن‌های زیبایی شما
                </p>
              </div>
              <Button
                disabled
                variant="default"
                onClick={() => setShowAddModal(true)}
                iconName="Plus"
                iconPosition="right"
                className="w-full sm:w-auto">
                افزودن سالن جدید
              </Button>
            </div>
          </div>

          {/* Summary Metrics */}
          <SummaryMetrics metrics={summaryMetrics} />

          {/* Search and Filter */}
          {/* <SearchAndFilter
            onSearch={handleSearch}
            onFilter={handleFilter}
            totalCount={filteredSalons?.length}
          /> */}

          {/* Salons Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSalons?.map((salon) => (
              <SalonCard
                key={salon?._id ?? salon?.id}
                salon={salon}
                onEdit={handleEditSalon}
                onDelete={handleDeleteSalon}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Empty State */}
          {filteredSalons?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon
                  name="Store"
                  size={32}
                  className="text-muted-foreground"
                />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                {searchTerm || activeFilter !== 'all'
                  ? 'سالنی یافت نشد'
                  : 'هنوز سالنی اضافه نکرده‌اید'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm || activeFilter !== 'all'
                  ? 'لطفاً جستجو یا فیلتر خود را تغییر دهید'
                  : 'اولین سالن خود را اضافه کنید تا شروع کنید'}
              </p>
              {!searchTerm && activeFilter === 'all' && (
                <Button
                  disabled
                  variant="default"
                  onClick={() => setShowAddModal(true)}
                  iconName="Plus"
                  iconPosition="right">
                  افزودن سالن جدید
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      {/* Modals */}
      <AddSalonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddSalon}
        ownerId={'66e78c12fa3a5f4bcd92ab11'}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        salon={selectedSalon}
      />
    </div>
  );
};

export default SalonDashboard;
